import { defineStore } from 'pinia';
import { useStorage } from '@vueuse/core';
import { useCookies } from '@vueuse/integrations/useCookies';
import apiRequest from '@/service/api/ApiRequestService';

const isLoggedInRef = useStorage('isLoggedIn');
const tokenExpiryRef = useStorage('tokenExpiry');
const { set, remove } = useCookies([], { doNotParse: false, autoUpdateDependencies: false });

export const UserStore = defineStore({
    id: 'user',

    state: () => ({
        user_data: {
            failed_login: 0,
            success_login: false,
            success_forgetpass: false,
            error_forgetpass: false,
            forgetpass_message: '',
            success_resetpass: false,
            error_resetpass: false,
            success_bank_updates: false,
            error_bank_updates: false,
            errors: false,
            userInfo: false,
            userBankInfo: false
        },
        filterVersion: 'Advanced',
        allSelectableLeadsColumns: [],
        userSelectedLeadsColumns: []
    }),

    getters: {
        // getUserData: () => useStorage(),
        isLoggedIn: () => (isLoggedInRef.value == 'true' || isLoggedInRef.value == true ? true : false) && Math.floor(new Date().getTime() / 1000) < tokenExpiryRef.value,
        getUserInfo: (state) => state.user_data.userInfo,
        getUserBankInfo: (state) => state.user_data.userBankInfo
        // isLoggedIn: () => true
    },

    actions: {
        setUserDataIfloggedIn() {
            let userData = useStorage('user_data');
            if (this.isLoggedIn && userData.value) {
                try {
                    this.user_data.userInfo = JSON.parse(userData.value);
                    // load bank info from api it's cached for 1h, and reset each time user make updates to it
                    this.loadBankInfo();
                } catch (e) {
                    console.error('Failed to parse user_data from storage', e);
                }
            }
        },
        setUserInfo(data) {
            this.user_data.userInfo = { ...data };
            // console.log(data);
            localStorage.removeItem('user_data');
            useStorage(
                'user_data',
                JSON.stringify({
                    ...data
                })
            );
        },
        setAllSelectedLeadColumns(selected_lead_columns) {
            this.allSelectableLeadsColumns = selected_lead_columns;
        },
        applyServerSelectedLeadColumns(serverSelectedColumns = []) {
            let hasSaved = false;
            try {
                const saved = JSON.parse(localStorage.getItem('saved_selected_leads_columns'));
                hasSaved = Array.isArray(saved) && saved.length > 0;
            } catch (_) {
                hasSaved = false;
            }

            const currentEmpty = !Array.isArray(this.userSelectedLeadsColumns) || this.userSelectedLeadsColumns.length === 0;
            const defaultCols = this.getDefaultLeadColumns();
            const defaultEmpty = !Array.isArray(defaultCols) || defaultCols.length === 0;

            if (currentEmpty && !hasSaved) {
                if (!defaultEmpty) {
                    this.userSelectedLeadsColumns = defaultCols;
                } else if (Array.isArray(serverSelectedColumns) && serverSelectedColumns.length > 0) {
                    this.userSelectedLeadsColumns = serverSelectedColumns;
                }
                localStorage.setItem('saved_selected_leads_columns', JSON.stringify(this.userSelectedLeadsColumns));
            }
        },
        getDefaultLeadColumns() {
            const preferredNames = ['campaignName', 'timestamp', 'leadID', 'status', 'payout', 'retained_date', 'keep_status', 'firstName', 'lastName', 'firstname', 'lastname', 'phone', 'email', 'utm_campaign_fb'];
            const preferredSet = new Set(preferredNames.map((n) => (n || '').toLowerCase()));
            const options = Array.isArray(this.allSelectableLeadsColumns) ? this.allSelectableLeadsColumns : [];
            return options.filter((opt) => preferredSet.has(String(opt?.name ?? opt?.code ?? '').toLowerCase()));
        },
        setUserSelectedLeadsColumns(hotreload = false) {
            // If user selection is empty, initialize from local storage; otherwise use curated defaults
            if (this.userSelectedLeadsColumns.length === 0 && !hotreload) {
                const saved = JSON.parse(localStorage.getItem('saved_selected_leads_columns'));
                if (Array.isArray(saved) && saved.length > 0) {
                    this.userSelectedLeadsColumns = saved;
                } else {
                    this.userSelectedLeadsColumns = this.getDefaultLeadColumns();
                }
            }

            // Persist when changed
            const current = JSON.stringify(this.userSelectedLeadsColumns);
            if (current !== localStorage.getItem('saved_selected_leads_columns')) {
                localStorage.setItem('saved_selected_leads_columns', current);
            }
        },
        async login(email, password, remember = false) {
            try {
                await apiRequest
                    .post('login', {
                        email: email,
                        password: password,
                        remember: remember
                    })
                    .then((response) => {
                        if (response.status === 'success') {
                            // If we have data, we treat it as a successful login
                            if (response.data && response.data.token) {
                                this.clearStorage();

                                const expiresInDays = remember ? 7 : 1;
                                remove('token');
                                set('token', response.data.token, {
                                    path: '/',
                                    expires: new Date(new Date().getTime() + expiresInDays * 24 * 60 * 60 * 1000)
                                });
                                isLoggedInRef.value = true;
                                tokenExpiryRef.value = Math.floor(new Date().getTime() / 1000) + (remember ? 7 * 24 * 60 * 60 : 24 * 60 * 60);

                                this.user_data.failed_login = 0;
                                this.user_data.success_login = true;

                                localStorage.removeItem('user_data');
                                useStorage(
                                    'user_data',
                                    JSON.stringify({
                                        name: response.data.name,
                                        email: response.data.email,
                                        address: response.data.address,
                                        country: response.data.country,
                                        zip: response.data.zip,
                                        company_name: response.data.company_name,
                                        phone: response.data.phone,
                                        send_email: response.data.send_email,
                                        accountType: response.data.accountType,
                                        bankDetail: response.data.bankDetail,
                                        metaData: response.data.metaData,
                                        manager: response.data.manager,
                                        impersonating: response.data.impersonating,
                                        role: response.data.role,
                                        owner_affiliate_id: response.data.owner_affiliate_id
                                    })
                                );
                                this.setUserData(response.data);
                                this.setBankInfo(response.data.bankDetail);
                                return;
                            }

                            this.user_data.failed_login++;
                        } else if (response.status === 'redirect') {
                            // Handle buyer redirect to buyer app
                            this.clearStorage();

                            if (response.redirect_url) {
                                console.log('Redirecting buyer to buyer app:', response.redirect_url);
                                // Store redirect info for potential debugging
                                localStorage.setItem(
                                    'buyer_redirect_info',
                                    JSON.stringify({
                                        timestamp: new Date().toISOString(),
                                        email: email,
                                        redirect_url: response.redirect_url
                                    })
                                );

                                // Redirect to buyer app
                                window.location.href = response.redirect_url;
                                return; // Don't continue processing
                            } else {
                                console.error('Redirect response missing redirect_url');
                                this.user_data.failed_login++;
                                throw new Error('Redirect URL not provided');
                            }
                        } else {
                            this.user_data.failed_login++;
                            this.user_data.errors = response?.message;
                        }
                    });
            } catch (error) {
                console.log('Login error caught:', error); // Debug log
                console.log('Error response:', error.response); // Debug log

                // Check if it's a buyer redirect that was caught as an error
                if (error.response && error.response.data && error.response.data.status === 'redirect') {
                    const redirectData = error.response.data;
                    this.clearStorage();

                    if (redirectData.redirect_url) {
                        console.log('Redirecting buyer to buyer app (from error):', redirectData.redirect_url);
                        localStorage.setItem(
                            'buyer_redirect_info',
                            JSON.stringify({
                                timestamp: new Date().toISOString(),
                                email: email,
                                redirect_url: redirectData.redirect_url
                            })
                        );

                        window.location.href = redirectData.redirect_url;
                        return;
                    }
                }

                console.error('Login request error:', error);
                this.user_data.failed_login++;
            }
        },
        async signup(data) {
            return await apiRequest.post('register', {
                email: data.email,
                password: data.password,
                password_confirmation: data.password,
                name: `${data.firstName} ${data.lastName}`,
                address: data.address,
                phone: data.phone,
                zip: data.zipCode,
                company_name: data.companyName,
                country: data.country,
                bank_name: data.bankName,
                bank_account_name: data.beneficiaryName,
                bank_account_number: data.accountNumber,
                bank_swift_code: data.swiftCode,
                acceptedTerms: data.acceptedTerms,
                beneficiary_name: data.beneficiaryName,
                routing_code: data.routingCode,
                bank_address: data.bankAddress,
                bank_country: data.bankCountry
            });
        },
        async logOutFromServer() {
            try {
                await apiRequest.post('logout');
            } catch (error) {
                console.error('Invalid request:', error);
            }
        },
        clearStorage() {
            //clear user info
            this.user_data.userInfo = false;
            //clear bank info (important for impersonation)
            this.user_data.userBankInfo = false;
            //iterate over all local storage data and remove them
            for (const lsKey of Object.keys(localStorage)) {
                if (!['theme_preference', 'report_timezone', 'viewedVersion'].includes(lsKey)) {
                    localStorage.removeItem(lsKey);
                    // console.log('removed:', lsKey);
                }
            }
            useStorage('user_data', null);
            //destroy isLoggedIn, token
            isLoggedInRef.value = false;
            tokenExpiryRef.value = null;
            //clear all cookies
            document.cookie.split(';').forEach(function (c) {
                document.cookie = c.replace(/^ +/, '').replace(/=.*/, '=;expires=' + new Date().toUTCString() + ';path=/');
            });
            remove('token', {
                path: '/'
            });
            remove('token');
        },
        setUserData(data) {
            this.user_data.userInfo = data;
        },
        setBankInfo(data) {
            this.user_data.userBankInfo = data;
        },
        logOut() {
            //log out from server
            this.logOutFromServer();

            //clear storage
            this.clearStorage();
            return true;
        },
        /**
         * Request a password reset link.
         * Handles both success and error responses from the API.
         *
         * @param {string} email - The user's email address
         */
        async forgotPassword(email) {
            // Reset state before making request
            this.user_data.success_forgetpass = false;
            this.user_data.error_forgetpass = false;
            this.user_data.forgetpass_message = '';

            try {
                const response = await apiRequest.post('forgot-password', { email });

                if (response.status === 'success') {
                    this.user_data.success_forgetpass = true;
                    this.user_data.forgetpass_message = response.message || 'Password reset link sent successfully.';
                } else {
                    this.user_data.error_forgetpass = true;
                    this.user_data.forgetpass_message = response.message || 'An error occurred. Please try again.';
                }
            } catch (error) {
                this.user_data.error_forgetpass = true;

                // Handle error response from API (e.g., rate limiting)
                if (error.response && error.response.data) {
                    this.user_data.forgetpass_message = error.response.data.message || 'An error occurred. Please try again.';
                } else if (error.message) {
                    this.user_data.forgetpass_message = error.message;
                } else {
                    this.user_data.forgetpass_message = 'Unable to process your request. Please try again later.';
                }
            }
        },
        /**
         * Validate a password reset token before showing the reset form.
         * This is a security measure to prevent showing the form for invalid/expired tokens.
         *
         * @param {string} token - The reset token from the URL
         * @returns {Promise<{status: string, message: string, data?: {email_masked: string, expires_in_minutes: number}}>}
         */
        async validateResetToken(token) {
            try {
                const response = await apiRequest.post('validate-reset-token', { token });
                return response;
            } catch (error) {
                // Return error response format
                if (error.response && error.response.data) {
                    return error.response.data;
                }
                return {
                    status: 'error',
                    message: 'Unable to validate reset link. Please try again.'
                };
            }
        },
        /**
         * Reset password using only the token (no email required).
         * The backend will look up the email from the token.
         *
         * @param {Object} formData - { token, password, password_confirmation }
         */
        async resetPassword(formData) {
            // Reset state before making request
            this.user_data.success_resetpass = false;
            this.user_data.error_resetpass = false;
            this.user_data.errors = false;

            try {
                const response = await apiRequest.post('reset-password', { ...formData });

                if (response.status === 'success') {
                    this.user_data.success_resetpass = true;
                } else {
                    this.user_data.errors = response.message || 'An error occurred. Please try again.';
                    this.user_data.error_resetpass = true;
                }
            } catch (error) {
                this.user_data.error_resetpass = true;

                // Handle error response from API (e.g., rate limiting, validation errors)
                if (error.response && error.response.data) {
                    this.user_data.errors = error.response.data.message || 'An error occurred. Please try again.';
                } else if (error.message) {
                    this.user_data.errors = error.message;
                } else {
                    this.user_data.errors = 'Unable to reset password. Please try again later.';
                }
            }
        },
        async updateUserInfo(formData) {
            try {
                await apiRequest
                    .post('profile/update', {
                        ...formData
                    })
                    .then((response) => {
                        if (response.status === 'success') {
                            this.user_data.success_resetpass = true;
                        } else {
                            this.user_data.errors = response.message;
                            this.user_data.error_resetpass = true;
                        }
                    });
            } catch (error) {
                console.log(error);
                console.error('Invalid request:', error);
            }
        },
        async updateBankInfo(formData) {
            try {
                await apiRequest
                    .post('profile/update', {
                        ...formData
                    })
                    .then((response) => {
                        if (response.status === 'success') {
                            console.log(response);
                            this.user_data.success_bank_updates = true;
                        } else {
                            this.user_data.success_bank_updates = false;
                            this.user_data.errors = response.message;
                            this.user_data.error_bank_updates = true;
                        }

                        return response;
                    });
            } catch (error) {
                console.log(error);
                console.error('Invalid request:', error);
            }
        },
        async loadBankInfo() {
            // create form data object
            let formData = {
                update_type: 'affiliate_profile'
            };

            try {
                await apiRequest
                    .post('profile/update', {
                        ...formData
                    })
                    .then((response) => {
                        if (response.status === 'success') {
                            this.user_data.userBankInfo = response.data;
                        } else {
                            this.user_data.errors = response.message;
                        }
                    });
            } catch (error) {
                console.log(error);
                console.error('Invalid request:', error);
            }
        }
    }
});

export default UserStore;
